import { Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ARMED_DELETE_PAINT } from '@/components/ui/armed-delete';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import { api } from '@/lib/api';
import { relativeTime } from '@/lib/relative-time';

/**
 * The devices that skip the emailed code, and the only way to withdraw that.
 *
 * A trust feature with no way to untrust is not finishable — and this list is the whole of it: there
 * is no admin revoke, because every Agavi account is one person. Self-revoke is the requirement.
 */

interface TrustedDevice {
  deviceId: string;
  userAgent: string;
  lastSeenAt: string;
  expiresAt: string;
  current: boolean;
}

/**
 * A user-agent string is unreadable, and the row exists so a person can RECOGNISE which machine
 * this is. Browser and platform is as much as anyone needs to point at one and say "that's the
 * laptop at the visitor centre".
 *
 * Deliberately coarse and never gated on: Chrome rewrites its version string every few weeks, so
 * this is for reading, not for matching.
 */
function describeDevice(ua: string): string | null {
  if (!ua) return null;
  const browser = /Edg\//.test(ua) ? 'Edge'
    : /OPR\//.test(ua) ? 'Opera'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Safari\//.test(ua) ? 'Safari'
    : null;
  const platform = /iPhone|iPad|iPod/.test(ua) ? 'iOS'
    : /Android/.test(ua) ? 'Android'
    : /Mac OS X/.test(ua) ? 'macOS'
    : /Windows/.test(ua) ? 'Windows'
    : /Linux/.test(ua) ? 'Linux'
    : null;
  if (browser && platform) return `${browser} · ${platform}`;
  return browser ?? platform;
}

export default function SignedInDevices() {
  const { t, i18n } = useTranslation();
  const [devices, setDevices] = useState<TrustedDevice[] | null>(null);
  const [armedId, setArmedId] = useState<string | null>(null);
  const [allArmed, setAllArmed] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setDevices(await api.get<TrustedDevice[]>('/trusted-devices'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load devices');
      setDevices([]);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function revoke(deviceId: string) {
    setError('');
    try {
      await api.delete(`/trusted-devices/${deviceId}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign out device');
    }
  }

  async function revokeAll() {
    setError('');
    try {
      await api.delete('/trusted-devices/all');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign out devices');
    }
  }

  if (devices === null) return <p className='text-sm text-ws-muted'>{t('profile.devices.loading')}</p>;

  if (devices.length === 0) {
    return (
      <>
        {error && <p role='alert' className='mb-2 text-sm text-ws-error-fg'>{error}</p>}
        <p className='text-sm text-ws-muted'>{t('profile.devices.empty')}</p>
      </>
    );
  }

  const locale = i18n.language || 'en';

  return (
    <div className='space-y-3'>
      {error && <p role='alert' className='text-sm text-ws-error-fg'>{error}</p>}

      <ul className='space-y-2'>
        {devices.map((d) => {
          const isArmed = armedId === d.deviceId;
          const name = describeDevice(d.userAgent) ?? t('profile.devices.unknownDevice');
          return (
            <li
              key={d.deviceId}
              className={`flex items-center gap-3 rounded-lg border bg-ws-bg px-4 py-3 transition ${
                isArmed
                  ? `border-ws-warning-fg ${ARMED_DELETE_PAINT}`
                  : 'border-ws-border'
              }`}
            >
              <div className='min-w-0 flex-1'>
                <p className='flex items-center gap-2 truncate text-sm font-medium text-ws-text'>
                  {name}
                  {d.current && <span className='shrink-0 text-xs font-medium text-ws-primary dark:text-ws-text-link'>{t('profile.devices.thisDevice')}</span>}
                </p>
                <p className='text-xs text-ws-muted'>
                  {t('profile.devices.lastUsed', { when: relativeTime(d.lastSeenAt, locale) })}
                  {' · '}
                  {t('profile.devices.expires', { when: relativeTime(d.expiresAt, locale) })}
                </p>
              </div>
              <IconButton
                variant='destructive'
                size='sm'
                isArmed={isArmed}
                aria-label={isArmed ? t('profile.devices.confirmSignOut', { device: name }) : t('profile.devices.signOut', { device: name })}
                onClick={() => { if (isArmed) { setArmedId(null); void revoke(d.deviceId); } else setArmedId(d.deviceId); }}
                onPointerLeave={(e) => { if (isArmed && e.pointerType !== 'touch') setArmedId(null); }}
                onBlur={() => { if (isArmed) setArmedId(null); }}
              >
                <Trash2 className='h-3.5 w-3.5' />
              </IconButton>
            </li>
          );
        })}
      </ul>

      {/* `warning`, which is not "destructive" but "this reaches further than the control you are
          standing on" — one click here untrusts machines that are not in front of you. */}
      <div className='flex justify-end'>
        <Button
          variant='warning'
          armed={allArmed}
          onClick={() => { if (allArmed) { setAllArmed(false); void revokeAll(); } else setAllArmed(true); }}
          onPointerLeave={(e) => { if (allArmed && e.pointerType !== 'touch') setAllArmed(false); }}
          onBlur={() => setAllArmed(false)}
        >
          {allArmed ? t('profile.devices.confirmSignOutAll') : t('profile.devices.signOutAll')}
        </Button>
      </div>
    </div>
  );
}
